import { useEffect } from "react";
import { Platform } from "react-native";

type Sentinel = {
  released: boolean;
  release: () => Promise<void>;
  addEventListener: (type: "release", listener: () => void) => void;
};
type WakeLockApi = { request: (type: "screen") => Promise<Sentinel>; };

const isWakeLockApi = (value: unknown): value is WakeLockApi =>
  typeof value === "object" && value !== null && "request" in value
  && typeof value.request === "function";

/**
 * Keeps the screen awake while `active`, for a board nobody touches. The browser releases the lock
 * when the tab is hidden or the OS reclaims it, so this asks again on `release` and when the page
 * becomes visible. Where the API is missing (older Safari) it does nothing, and the Vendor's setup
 * card tells them to set Auto-Lock to Never.
 */
export function useWakeLock(active: boolean) {
  useEffect(() => {
    if (!active || Platform.OS !== "web" || typeof navigator === "undefined") return undefined;
    const wakeLock: unknown = Reflect.get(navigator, "wakeLock");
    if (!isWakeLockApi(wakeLock)) return undefined;
    let sentinel: Sentinel | null = null;
    let stopped = false;
    const acquire = async () => {
      if (stopped || document.visibilityState !== "visible" || (sentinel && !sentinel.released)) {
        return;
      }
      try {
        const next = await wakeLock.request("screen");
        if (stopped) {
          void next.release();
          return;
        }
        sentinel = next;
        next.addEventListener("release", () => void acquire());
      } catch {
        // Denied (low battery, hidden tab): the next visibilitychange asks again.
      }
    };
    const onVisible = () => void acquire();
    document.addEventListener("visibilitychange", onVisible);
    void acquire();
    return () => {
      stopped = true;
      document.removeEventListener("visibilitychange", onVisible);
      void sentinel?.release();
    };
  }, [active]);
}
